import { useMemo } from 'react'
import { Text, View, type ViewProps } from 'react-native'
import { cva } from 'class-variance-authority'

import type { Mood } from '@/types/common'
import { cn } from '@/utils/cn'
import { CheckCircle, Error, Info, Warning } from '@/components/icon'

const viewVariants = cva('flex-row gap-2 rounded-lg p-3', {
  variants: {
    mood: {
      neutral: 'bg-fill-light',
      success: 'bg-success-light',
      error: 'bg-danger-light',
      info: 'bg-info-light',
      warning: 'bg-warning-light'
    }
  },
  defaultVariants: {
    mood: 'warning'
  }
})

const iconVariants = cva('size-5', {
  variants: {
    mood: {
      neutral: 'text-text-primary',
      success: 'text-success',
      error: 'text-danger',
      info: 'text-info',
      warning: 'text-warning'
    }
  },
  defaultVariants: {
    mood: 'warning'
  }
})

interface BannerProps extends ViewProps {
  mood?: Mood
  title?: string
  children: string
}

export function Banner({ title, children, mood = 'warning' }: BannerProps) {
  const Icon = useMemo(() => {
    switch (mood) {
      case 'success': {
        return CheckCircle
      }
      case 'error': {
        return Error
      }
      case 'info': {
        return Info
      }
      case 'warning': {
        return Warning
      }
      default: {
        return Info
      }
    }
  }, [mood])
  return (
    <View className={cn(viewVariants({ mood }))}>
      <Icon className={cn(iconVariants({ mood }))} />
      <View className={'flex-1 gap-1'}>
        {title && <Text className={'text-sm font-semibold text-text-primary'}>{title}</Text>}
        <Text className={'text-sm text-text-primary'}>{children}</Text>
      </View>
    </View>
  )
}
